import { Injectable, computed, inject, signal } from '@angular/core';
import { UserService } from '@cadriciel/core';

/**
 * Libellés parlants associés aux codes de rôle techniques.
 * Tout code absent de ce dictionnaire est affiché tel quel.
 */
export const ROLE_LABELS: Record<string, string> = {
    GUEST: 'Invité',
    ROLE_APP_ADMIN: 'Administrateur application',
    ROLE_CUSTOMER_ADMIN: 'Administrateur client',
};

/** Code du rôle administrateur client (accès recette / présentation). */
export const ROLE_CUSTOMER_ADMIN = 'ROLE_CUSTOMER_ADMIN';

const ACTIVE_ROLE_STORAGE_KEY = 'activeRole';

/**
 * Gère le « rôle actif » de l'utilisateur — uniquement côté affichage / navigation.
 *
 * L'utilisateur peut posséder plusieurs rôles (`roleActifList`). Ce service
 * mémorise lequel est actuellement sélectionné pour adapter l'interface
 * (libellé affiché, liens visibles, accès aux pages via {@link activeRoleGuard}).
 * Les permissions backend ne sont pas affectées : toutes les autorités de
 * l'utilisateur restent actives côté serveur.
 *
 * `activeRole` est un computed pur : il vaut toujours le choix de l'utilisateur
 * s'il est valide, sinon le premier rôle disponible. Pas d'effet, donc valeur
 * cohérente et synchrone dès que le profil est chargé.
 */
@Injectable({ providedIn: 'root' })
export class ActiveRoleService {
    private userService = inject(UserService);

    /** Tous les rôles de l'utilisateur courant. */
    readonly availableRoles = computed(() => this.userService.currentUser()?.roleActifList ?? []);

    /** Choix explicite de l'utilisateur (null = aucun choix → premier rôle par défaut). */
    private _selectedRole = signal<string | null>(this.readStoredRole());

    /** Rôle actif effectif : le choix s'il est valide, sinon le premier rôle disponible. */
    readonly activeRole = computed<string | null>(() => {
        const roles = this.availableRoles();
        const selected = this._selectedRole();
        if (selected && roles.includes(selected)) return selected;
        return roles[0] ?? null;
    });

    /** Libellé parlant du rôle actif. */
    readonly activeRoleLabel = computed(() => this.roleLabel(this.activeRole()));

    /** Vrai quand le rôle actif est l'administrateur client. */
    readonly isCustomerAdminActive = computed(() => this.activeRole() === ROLE_CUSTOMER_ADMIN);

    /** Renvoie le libellé parlant d'un code de rôle. */
    roleLabel(code: string | null | undefined): string {
        if (!code) return 'Utilisateur';
        return ROLE_LABELS[code] ?? code;
    }

    /** Sélectionne le rôle actif et le persiste. */
    setActiveRole(code: string): void {
        this._selectedRole.set(code);
        try {
            localStorage.setItem(ACTIVE_ROLE_STORAGE_KEY, code);
        } catch {
            // localStorage indisponible (SSR / navigation privée) — on garde l'état en mémoire.
        }
    }

    private readStoredRole(): string | null {
        try {
            return localStorage.getItem(ACTIVE_ROLE_STORAGE_KEY);
        } catch {
            return null;
        }
    }
}
